import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { finalizeBilling } from '@/lib/api/billing';
import { onboardMyTenant, TENANT_ME_QUERY_KEY } from '@/lib/api/tenants';

/**
 * Inner Step 5 form — must render inside an `<Elements>` provider so
 * `useStripe` / `useElements` resolve. Owns the card-capture →
 * finalize → onboard → dashboard flow.
 *
 * Finish:
 *   1. `stripe.confirmSetup({ redirect: 'if_required' })` — confirms
 *      the SetupIntent. `if_required` keeps the merchant in-page for
 *      non-3DS cards; 3DS cards get Stripe's SCA modal, then resolve
 *      here without a full-page redirect.
 *   2. `POST /billing/finalize` with the captured PaymentMethod id —
 *      backend sets it as default + creates the trial Subscription.
 *   3. `POST /tenants/me/onboard` — marks onboarding complete (the
 *      same endpoint the old Step 5 Finish used, PR 27).
 *   4. Invalidate the tenant cache, navigate to `/app`.
 */
export function Step5BillingForm() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);

  async function finish() {
    if (!stripe || !elements) return;
    setTopError(null);
    setSubmitting(true);
    try {
      const confirm = await stripe.confirmSetup({ elements, redirect: 'if_required' });
      if (confirm.error) {
        setTopError(
          confirm.error.message ?? "We couldn't confirm your card. Please check the details.",
        );
        return;
      }
      const paymentMethod = confirm.setupIntent.payment_method;
      const paymentMethodId =
        typeof paymentMethod === 'string' ? paymentMethod : (paymentMethod?.id ?? null);
      if (!paymentMethodId) {
        setTopError("We couldn't read your card details. Please try again.");
        return;
      }

      const finalized = await finalizeBilling(paymentMethodId);
      if (finalized.isErr()) {
        setTopError(billingErrorMessage(finalized.error.kind));
        return;
      }

      const onboarded = await onboardMyTenant();
      if (onboarded.isErr()) {
        setTopError(billingErrorMessage(onboarded.error.kind));
        return;
      }

      await qc.invalidateQueries({ queryKey: TENANT_ME_QUERY_KEY });
      void navigate('/app');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      {topError ? (
        <Alert variant="destructive">
          <AlertDescription>{topError}</AlertDescription>
        </Alert>
      ) : null}

      {/* Suppress Stripe's auto-rendered card mandate text — we show
          our own single, trial-specific authorisation line below. */}
      <PaymentElement options={{ terms: { card: 'never' } }} />

      <p className="text-muted-foreground text-xs">
        By providing your card information, you authorise Mailchute.ai to charge your card for
        shipments on a monthly basis after the trial has ended. You can cancel anytime before the
        trial has ended and you won&apos;t be charged. Fair use policy applies for the duration of
        the trial.
      </p>

      <div className="flex justify-between">
        <Button
          type="button"
          variant="ghost"
          disabled={submitting}
          onClick={() => {
            void navigate('/app/onboarding/step/4');
          }}
        >
          Back
        </Button>
        <Button
          type="button"
          disabled={submitting}
          onClick={() => {
            void finish();
          }}
        >
          {submitting ? 'Starting trial…' : 'Start 7-day trial'}
        </Button>
      </div>
    </div>
  );
}

function billingErrorMessage(
  kind: 'unauthorised' | 'not_found' | 'validation' | 'server_error',
): string {
  if (kind === 'unauthorised') return 'Your session expired. Please sign in again.';
  return "We couldn't start your trial. Please try again.";
}
